// 持卡人邮箱验证 PAGE-CHD-AUTH-002
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';
import { useToast } from '@/components/Toast';
import { fetchMe, logout, sendEmailCode, verifyEmailCode } from '@/api/auth';
import { loadSession, saveSession } from '@/api/client';
import { useLocalePath } from '@/i18n/useLocalePath';
import { mapApiError } from '@/lib/apiErrorMessages';

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { localePath: lp } = useLocalePath();
  const session = loadSession();
  const [masked, setMasked] = useState(session?.me?.emailMasked || session?.principal || '');
  const [otp, setOtp] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!session?.accessToken) {
      navigate(lp('/login'), { replace: true });
      return;
    }
    void fetchMe().then((res) => {
      if (res.ok) {
        if (res.data.emailVerified) {
          navigate(lp('/cardholder/dashboard'), { replace: true });
          return;
        }
        setMasked(res.data.emailMasked || res.data.principal || '');
        const cur = loadSession();
        if (cur) saveSession({ ...cur, me: res.data });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);

  const send = async () => {
    setBusy(true);
    try {
      const res = await sendEmailCode();
      if (!res.ok) {
        toast.error(t(mapApiError(res.code, res.error)));
        return;
      }
      setMasked(res.data.emailMasked || masked);
      setCooldown(res.data.cooldownSeconds ?? 60);
      toast.success(`验证码已发送${res.data.sentVia ? `（${res.data.sentVia}）` : ''}`);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp.trim())) {
      toast.error('请输入 6 位数字验证码');
      return;
    }
    setBusy(true);
    try {
      const res = await verifyEmailCode(otp.trim());
      if (!res.ok) {
        toast.error(t(mapApiError(res.code, res.error)));
        return;
      }
      const me = await fetchMe();
      const cur = loadSession();
      if (cur && me.ok) saveSession({ ...cur, me: me.data });
      toast.success('邮箱验证成功');
      navigate(lp('/cardholder/dashboard'), { replace: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <BrandLogo to={lp('/')} variant="dark" size="lg" className="justify-center" />
        </div>
        <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
          <div className="flex items-center gap-2 text-white mb-2">
            <ShieldCheck className="w-5 h-5 text-brand-400" />
            <h1 className="text-lg font-semibold">验证邮箱</h1>
          </div>
          <p className="text-sm text-slate-400 mb-6">
            验证码将发送至 <span className="text-slate-200 font-medium">{masked || '您的邮箱'}</span>。
            验证通过后才能进行 KYC、开卡与资金操作。
          </p>
          <form onSubmit={(e) => void verify(e)} className="space-y-4">
            <input
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full tracking-[0.4em] text-center text-2xl font-mono py-3 rounded-xl bg-white/5 border border-white/10 text-white outline-none focus:border-brand-400"
              placeholder="••••••"
            />
            <div className="flex gap-2">
              <button
                type="button"
                disabled={busy || cooldown > 0}
                onClick={() => void send()}
                className="flex-1 min-h-[44px] rounded-xl border border-white/15 text-slate-200 text-sm font-semibold disabled:opacity-50"
              >
                {cooldown > 0 ? `${cooldown}s 后重发` : '发送验证码'}
              </button>
              <button
                type="submit"
                disabled={busy}
                className="flex-1 min-h-[44px] rounded-xl bg-brand-gradient text-white text-sm font-semibold disabled:opacity-60"
              >
                确认验证
              </button>
            </div>
          </form>
          <div className="mt-5 flex justify-between text-xs text-slate-500">
            <button type="button" className="hover:text-slate-300" onClick={() => void logout()}>
              退出登录
            </button>
            <Link to={lp('/cardholder/dashboard')} className="hover:text-slate-300">
              稍后验证（仅可浏览）
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
